import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { Task } from '../../types/todoTypes';
import { getStatusColor, truncateText, formatRelativeTime } from '../../utils/todoUtils';

interface TaskCardProps {
  task: Task;
  onClick: (task: Task) => void;
  onDelete: (task: Task) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, onClick, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleMenuClose();
    onDelete(task);
  };

  const handleCardClick = () => {
    onClick(task);
  };

  return (
    <>
      <Card
        sx={{
          cursor: 'pointer',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: 4,
            transform: 'translateY(-2px)',
          },
        }}
        onClick={handleCardClick}
      >
        <CardContent sx={{ flexGrow: 1, position: 'relative', pb: 1 }}>
          {/* More menu button */}
          <IconButton
            size="small"
            onClick={handleMenuClick}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>

          {/* Creation timestamp */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            {formatRelativeTime(task.createdAt)}
          </Typography>

          {/* Task title */}
          <Typography
            variant="h6"
            component="h3"
            sx={{
              mb: 1,
              fontSize: '1rem',
              fontWeight: 600,
              pr: 3,
              wordBreak: 'break-word',
            }}
          >
            {truncateText(task.title, 50)}
          </Typography>

          {/* Task description (truncated) */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1.5,
              wordBreak: 'break-word',
            }}
          >
            {truncateText(task.description, 80)}
          </Typography>

          {/* Status badge */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Chip
              label={task.status}
              color={getStatusColor(task.status)}
              size="small"
              sx={{ fontWeight: 500 }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Context menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          Delete Task
        </MenuItem>
      </Menu>
    </>
  );
};

export default TaskCard;
